interface Product {
  id: string;
  title: string;
  description: string;
  price: number;
  currency: string;
  category: string;
  cover_image: string;
  media_files: Array<{ url: string; type: 'image' | 'video'; name: string }> | null;
  file_url: string;
  product_type: string;
  stock_unlimited: boolean;
  stock_quantity: number;
  shipping_info: string;
  weight: number;
}

interface StoreProfile {
  store_name: string;
  store_description: string;
  banner_image: string;
  logo_image: string;
  social_links?: Array<{ id: string; platform: string; label: string; url: string; is_active: boolean }>;
}

interface Profile {
  full_name: string;
  avatar_url: string;
}

interface Props {
  storeProfile: StoreProfile | null;
  userProfile: Profile | null;
  products: Product[];
  storeName: string;
  storeDesc: string;
  onBuyNow: (product: Product) => void;
  onCardClick: (product: Product) => void;
  searchQuery: string;
  setSearchQuery: (v: string) => void;
  activeFilter: 'all' | 'digital' | 'physical';
  setActiveFilter: (v: 'all' | 'digital' | 'physical') => void;
  filteredProducts: Product[];
}

function isVideoUrl(url: string): boolean {
  if (!url) return false;
  const lower = url.toLowerCase().split('?')[0];
  return lower.endsWith('.mp4') || lower.endsWith('.mov') || lower.endsWith('.webm') || lower.endsWith('.ogg');
}

const categoryIcons: Record<string, string> = {
  'e-book': 'ri-book-line', 'template': 'ri-file-text-line', 'course': 'ri-graduation-cap-line',
  'audio': 'ri-music-line', 'video': 'ri-video-line', 'software': 'ri-code-box-line',
  'clothing': 'ri-t-shirt-line', 'accessories': 'ri-handbag-line', 'art': 'ri-paint-brush-line',
  'electronics': 'ri-cpu-line', 'books': 'ri-book-open-line', 'other': 'ri-folder-line',
};

const LINK_PLATFORMS: Record<string, { icon: string; color: string }> = {
  website:   { icon: 'ri-global-line',           color: 'text-gray-500' },
  instagram: { icon: 'ri-instagram-line',         color: 'text-pink-500' },
  twitter:   { icon: 'ri-twitter-x-line',         color: 'text-gray-800' },
  facebook:  { icon: 'ri-facebook-circle-line',   color: 'text-blue-600' },
  tiktok:    { icon: 'ri-tiktok-line',            color: 'text-gray-900' },
  youtube:   { icon: 'ri-youtube-line',           color: 'text-red-500' },
  linkedin:  { icon: 'ri-linkedin-box-line',      color: 'text-blue-700' },
  whatsapp:  { icon: 'ri-whatsapp-line',          color: 'text-green-500' },
  telegram:  { icon: 'ri-telegram-line',          color: 'text-sky-500' },
  custom:    { icon: 'ri-links-line',             color: 'text-teal-500' },
};

export default function MinimalTemplate({ storeProfile, userProfile, products, storeName, storeDesc, onBuyNow, onCardClick, searchQuery, setSearchQuery, activeFilter, setActiveFilter, filteredProducts }: Props) {
  const digitalCount = products.filter(p => p.product_type !== 'physical').length;
  const physicalCount = products.filter(p => p.product_type === 'physical').length;
  const activeLinks = (storeProfile?.social_links || []).filter(l => l.is_active);

  return (
    <div className="min-h-screen bg-white font-sans">
      {/* Top bar */}
      <div className="border-b border-gray-100 py-5 px-8">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            {(storeProfile?.logo_image || userProfile?.avatar_url) && (
              <div className="w-10 h-10 rounded-full overflow-hidden border border-gray-200 flex-shrink-0">
                <img src={storeProfile?.logo_image || userProfile?.avatar_url || ''} alt="logo" className="w-full h-full object-cover" />
              </div>
            )}
            <span className="text-lg font-bold text-gray-900 tracking-tight">{storeName}</span>
          </div>
          <div className="flex items-center gap-2 text-xs text-gray-400">
            <i className="ri-shield-check-line text-gray-300"></i>
            Secure Store
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-8 py-14">
        {/* Hero text */}
        <div className="mb-14 max-w-2xl">
          <h1 className="text-5xl font-black text-gray-900 leading-tight tracking-tight mb-4">{storeName}</h1>
          <p className="text-lg text-gray-500 leading-relaxed">{storeDesc}</p>
          <div className="flex items-center gap-6 mt-6 text-sm text-gray-400">
            <span><strong className="text-gray-700">{products.length}</strong> Products</span>
            {digitalCount > 0 && <span><strong className="text-gray-700">{digitalCount}</strong> Digital</span>}
            {physicalCount > 0 && <span><strong className="text-gray-700">{physicalCount}</strong> Physical</span>}
          </div>
        </div>

        {/* Linktree Section */}
        {activeLinks.length > 0 && (
          <div className="mb-10 pb-8 border-b border-gray-100">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-4">Links</p>
            <div className="flex flex-wrap gap-3">
              {activeLinks.map(link => {
                const meta = LINK_PLATFORMS[link.platform] || LINK_PLATFORMS.custom;
                return (
                  <a
                    key={link.id}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-gray-200 bg-gray-50 hover:border-gray-400 hover:bg-white transition-all group cursor-pointer"
                  >
                    <i className={`${meta.icon} text-sm ${meta.color}`}></i>
                    <span className="text-xs font-semibold text-gray-700 group-hover:text-gray-900">{link.label}</span>
                    <i className="ri-external-link-line text-gray-300 text-xs"></i>
                  </a>
                );
              })}
            </div>
          </div>
        )}

        {/* Search + Filter */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-10 pb-8 border-b border-gray-100">
          <div className="relative flex-1 max-w-xs">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-300 text-sm"></i>
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search..."
              className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-gray-400 bg-white text-gray-800"
            />
          </div>
          <div className="flex items-center gap-2">
            {(['all', 'digital', 'physical'] as const).map(f => (
              <button
                key={f}
                onClick={() => setActiveFilter(f)}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap border ${activeFilter === f ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-500 border-gray-200 hover:border-gray-400'}`}
              >
                {f === 'all' ? `All` : f === 'digital' ? `Digital` : `Physical`}
              </button>
            ))}
          </div>
        </div>

        {/* Products */}
        {filteredProducts.length === 0 ? (
          <div className="py-24 text-center">
            <p className="text-2xl font-bold text-gray-200 mb-2">{searchQuery ? 'Nothing found' : 'No products yet'}</p>
            <p className="text-sm text-gray-400">{searchQuery ? `Try a different search.` : 'Check back soon.'}</p>
            {searchQuery && (
              <button onClick={() => setSearchQuery('')} className="mt-4 px-5 py-2 bg-gray-900 text-white rounded-full text-sm font-medium cursor-pointer whitespace-nowrap">
                Clear
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 pb-16">
            {filteredProducts.map(product => {
              const isPhysical = product.product_type === 'physical';
              const outOfStock = !product.stock_unlimited && product.stock_quantity <= 0;
              const firstMedia = product.media_files && product.media_files.length > 0 ? product.media_files[0] : null;
              const src = firstMedia ? firstMedia.url : product.cover_image;
              const isVid = firstMedia ? firstMedia.type === 'video' : isVideoUrl(product.cover_image);
              return (
                <div
                  key={product.id}
                  onClick={() => onCardClick(product)}
                  className="group cursor-pointer"
                >
                  <div className="relative overflow-hidden rounded-xl bg-gray-50 mb-4" style={{ minHeight: '200px' }}>
                    {src ? (
                      isVid ? (
                        <video src={src} className="w-full h-auto object-contain" muted autoPlay loop playsInline />
                      ) : (
                        <img src={src} alt={product.title} className="w-full h-auto object-contain group-hover:scale-105 transition-transform duration-500" />
                      )
                    ) : (
                      <div className="w-full h-48 flex flex-col items-center justify-center bg-gray-50">
                        <i className={`${categoryIcons[product.category] || 'ri-file-line'} text-5xl text-gray-200 mb-2`}></i>
                      </div>
                    )}
                    {outOfStock && (
                      <div className="absolute inset-0 bg-white/80 flex items-center justify-center">
                        <span className="text-sm font-bold text-gray-400 border border-gray-200 px-4 py-1.5 rounded-full">Out of Stock</span>
                      </div>
                    )}
                  </div>
                  <div>
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <h3 className="text-sm font-bold text-gray-900 line-clamp-2 leading-snug flex-1">{product.title}</h3>
                      <span className="text-xs text-gray-400 whitespace-nowrap capitalize">{isPhysical ? 'Physical' : 'Digital'}</span>
                    </div>
                    <p className="text-xs text-gray-400 line-clamp-2 mb-3">{product.description}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-base font-extrabold text-gray-900">{product.currency} {product.price.toLocaleString()}</span>
                      <button
                        onClick={e => { e.stopPropagation(); if (!outOfStock) onBuyNow(product); }}
                        disabled={outOfStock}
                        className={`text-xs font-semibold px-4 py-1.5 rounded-full transition-all cursor-pointer whitespace-nowrap ${outOfStock ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-gray-900 text-white hover:bg-gray-700'}`}
                      >
                        {outOfStock ? 'Unavailable' : isPhysical ? 'Buy' : 'Get'}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="border-t border-gray-100 py-8">
        <div className="max-w-5xl mx-auto px-8 flex items-center justify-between">
          <span className="text-xs text-gray-300">Powered by <strong className="text-gray-500">EventHub Store</strong></span>
          <div className="flex items-center gap-4 text-xs text-gray-300">
            <span className="flex items-center gap-1"><i className="ri-shield-check-line"></i> Secure</span>
            <span className="flex items-center gap-1"><i className="ri-lock-line"></i> Protected</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
